import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CreditCard, Smartphone, Shield, CheckCircle, Loader2, Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/hooks/useLanguage';
import { formatPrice as formatPriceUtil } from '@/utils/currency';
import { getPaymentProvider } from '@/services/paymentRouter';
import { StripeEmbeddedCheckout } from './StripeEmbeddedCheckout';
import { HostedPaymentCheckout } from './HostedPaymentCheckout';

type PaymentMethodType = 'stripe' | 'mobile';

interface SavedCard {
  id: string;
  card_brand: string | null;
  last_four: string | null;
  stripe_payment_method_id: string | null;
  is_default: boolean;
}

const FLUTTERWAVE_PUBLIC_KEY = import.meta.env.VITE_FLUTTERWAVE_PUBLIC_KEY || '';

interface PaymentMethodProps {
  totalAmount: number;
  onPaymentSuccess: () => void;
  onCancel: () => void;
}

const PaymentMethod = ({ 
  totalAmount, 
  onPaymentSuccess, 
  onCancel 
}: PaymentMethodProps) => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType>('stripe');
  const [availableProvider, setAvailableProvider] = useState<'both' | 'stripe' | 'flutterwave' | 'maishapay'>('both');
  const [loading, setLoading] = useState(false);
  const [cardProvider, setCardProvider] = useState<'stripe' | 'dusupay' | 'maishapay'>('stripe');
  const [mobileProvider, setMobileProvider] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [stripeFeePercentage, setStripeFeePercentage] = useState(1.6);
  const [maishapayCardFeePercentage, setMaishapayCardFeePercentage] = useState(2.0);
  const [dusupayCardFeePercentage, setDusupayCardFeePercentage] = useState(2.0);
  const [mobileMoneyFeePercentage, setMobileMoneyFeePercentage] = useState(2.0);
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [savedCards, setSavedCards] = useState<SavedCard[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string>('new');
  const [loadingCards, setLoadingCards] = useState(false);
  const [pollState, setPollState] = useState<null | 'polling' | 'timeout' | 'failed'>(null);
  const [pollTxRef, setPollTxRef] = useState<string>('');
  const [pollMessage, setPollMessage] = useState<string>('');
  const [sandboxBypassEnabled, setSandboxBypassEnabled] = useState(false);
  const [pollProvider, setPollProvider] = useState<'flutterwave' | 'maishapay' | 'dusupay'>('flutterwave');
  const [showHostedModal, setShowHostedModal] = useState(false);
  const [hostedCheckoutUrl, setHostedCheckoutUrl] = useState<string | null>(null);
  const [hostedProviderLabel, setHostedProviderLabel] = useState('Paiement');
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user } = useAuth();
  const { displayCurrency, convertPrice } = useCurrency();
  const { toast } = useToast();
  const { t } = useLanguage();

  const readNumericSetting = (raw: unknown, fallback: number) => {
    if (raw === null || raw === undefined) return fallback;
    if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsedJson = JSON.parse(raw);
        return readNumericSetting(parsedJson, fallback);
      } catch {
        // Fall back to direct numeric parsing for legacy plain-text values.
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    if (typeof raw === 'object' && raw !== null && 'value' in (raw as Record<string, unknown>)) {
      const nested = (raw as Record<string, unknown>).value;
      if (typeof nested === 'number' && Number.isFinite(nested)) return nested;
      if (typeof nested === 'string') {
        const parsed = Number(nested);
        return Number.isFinite(parsed) ? parsed : fallback;
      }
    }
    return fallback;
  };

  const openHostedPopup = (url: string) => {
    const popupWidth = 520;
    const popupHeight = 760;
    const left = Math.max(0, Math.round((window.screen.width - popupWidth) / 2));
    const top = Math.max(0, Math.round((window.screen.height - popupHeight) / 2));
    const popup = window.open(
      url,
      'nazandoo-payment',
      `popup=yes,width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (popup) {
      popup.focus();
      return true;
    }

    return false;
  };

  const canEmbedHostedCheckout = (url: string) => {
    try {
      const hostname = new URL(url).hostname.toLowerCase();
      return ![
        'testsecureacceptance.cybersource.com',
        'secureacceptance.cybersource.com',
      ].includes(hostname);
    } catch {
      return true;
    }
  };

  const formatDisplay = (cents: number) => {
    return formatPriceUtil(cents, displayCurrency);
  };

  const extractEdgeFunctionErrorMessage = async (error: any, fallback: string) => {
    let message = error?.message || fallback;
    const context = error?.context;

    if (context) {
      if (typeof context === 'string' && context.trim()) {
        message = context;
      }

      if (typeof context === 'object') {
        const directMessage = (context as any)?.error || (context as any)?.message || (context as any)?.details;
        if (directMessage) {
          message = String(directMessage);
        }

        if (typeof (context as any)?.json === 'function') {
          const body = await (context as any).json().catch(() => null);
          const bodyMessage = body?.error || body?.message || body?.details;
          if (bodyMessage) {
            message = String(bodyMessage);
          }
        } else if (typeof (context as any)?.text === 'function') {
          const raw = await (context as any).text().catch(() => '');
          if (raw) {
            try {
              const parsed = JSON.parse(raw);
              const parsedMessage = parsed?.error || parsed?.message || parsed?.details;
              if (parsedMessage) {
                message = String(parsedMessage);
              }
            } catch {
              if (raw.length < 240) {
                message = raw;
              }
            }
          }
        }
      }
    }

    if (message === 'Edge Function returned a non-2xx status code') {
      // Try to extract more detailed error info from context
      console.error('[Payment Error Details]', error);
      return 'Echec du provider de paiement. Verifie les cles/URL MaishaPay dans les secrets Supabase.';
    }

    return message;
  };

  // Load saved cards
  useEffect(() => {
    const loadSavedCards = async () => {
      if (!user) return;
      setLoadingCards(true);
      try {
        const { data, error } = await supabase
          .from('payment_methods')
          .select('id, card_brand, last_four, stripe_payment_method_id, is_default')
          .eq('user_id', user.id)
          .eq('type', 'card')
          .not('stripe_payment_method_id', 'is', null);
        
        if (error) throw error;
        setSavedCards(data || []);
        
        // Select default card if exists
        const defaultCard = data?.find(c => c.is_default);
        if (defaultCard) {
          setSelectedCardId(defaultCard.id);
        }
      } catch (error) {
        console.error('Error loading saved cards:', error);
      } finally {
        setLoadingCards(false);
      }
    };
    loadSavedCards();
  }, [user]);

  // Load default mobile money payment method
  useEffect(() => {
    const loadDefaultMobileMethod = async () => {
      if (!user) return;
      try {
        const { data } = await supabase
          .from('payment_methods')
          .select('provider, phone_number, is_default')
          .eq('user_id', user.id)
          .eq('type', 'mobile_money')
          .order('is_default', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (data?.phone_number) {
          setMobileProvider(data.provider || '');
          setPhoneNumber(data.phone_number);
          if (data.is_default) {
            setSelectedMethod('mobile');
          }
        }
      } catch (error) {
        console.error('Error loading default mobile method:', error);
      }
    };
    loadDefaultMobileMethod();
  }, [user]);

  // Load payment fees from settings
  useEffect(() => {
    const loadPaymentFees = async () => {
      const { data: stripeData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'stripe_fee_percentage')
        .maybeSingle();

      const { data: maishaCardData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'maishapay_card_fee_percentage')
        .maybeSingle();

      const { data: dusupayCardData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'dusupay_card_fee_percentage')
        .maybeSingle();
      
      const { data: mobileData } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'mobile_money_fee_percentage')
        .maybeSingle();

      setStripeFeePercentage(readNumericSetting(stripeData?.value, 1.6));
      setMaishapayCardFeePercentage(readNumericSetting(maishaCardData?.value, 2.0));
      setDusupayCardFeePercentage(readNumericSetting(dusupayCardData?.value, 2.0));
      setMobileMoneyFeePercentage(readNumericSetting(mobileData?.value, 2.0));

      const { data: cardProviderData } = await (supabase as any)
        .from('app_settings')
        .select('value')
        .eq('key', 'card_payment_provider')
        .maybeSingle();

      if (cardProviderData?.value === 'stripe' || cardProviderData?.value === 'dusupay' || cardProviderData?.value === 'maishapay') {
        setCardProvider(cardProviderData.value);
      }
    };

    loadPaymentFees();
  }, []);

  useEffect(() => {
    const loadRoutingPreference = async () => {
      try {
        const { data: simulationEnabledSetting } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'payment_country_simulation_enabled')
          .maybeSingle();

        const { data: simulationCountrySetting } = await supabase
          .from('site_settings')
          .select('value')
          .eq('key', 'payment_country_simulation_country')
          .maybeSingle();

        const simulationEnabled =
          simulationEnabledSetting?.value && typeof simulationEnabledSetting.value === 'object' && 'value' in simulationEnabledSetting.value
            ? Boolean(simulationEnabledSetting.value.value)
            : simulationEnabledSetting?.value === true;

        const simulationCountry =
          simulationCountrySetting?.value && typeof simulationCountrySetting.value === 'object' && 'value' in simulationCountrySetting.value
            ? String(simulationCountrySetting.value.value)
            : typeof simulationCountrySetting?.value === 'string'
            ? simulationCountrySetting.value
            : undefined;

        const provider = simulationEnabled && simulationCountry
          ? getPaymentProvider({ id: user?.id || 'anonymous', country: simulationCountry })
          : 'stripe';

        if (simulationEnabled && simulationCountry) {
          setAvailableProvider(provider);
          setSelectedMethod(provider === 'stripe' ? 'stripe' : 'mobile');
          return;
        }

        setAvailableProvider('both');
      } catch (error) {
        console.error('Error loading payment routing preference:', error);
        setAvailableProvider('both');
      }
    };

    loadRoutingPreference();
  }, [user?.id]);

  // Auto-select payment method when one provider is forced by routing
  useEffect(() => {
    if (availableProvider === 'flutterwave' || availableProvider === 'maishapay') {
      setSelectedMethod('mobile');
    } else if (availableProvider === 'stripe') {
      setSelectedMethod('stripe');
    }
    // If 'both', keep current selection
  }, [availableProvider]);

  // Listen for payment success and session requests
  useEffect(() => {
    const onMessage = async (event: MessageEvent) => {
      try {
        if (event.origin !== window.location.origin) return;
        const data = (event.data || {}) as any;
        if (data.type === 'PAYMENT_SUCCESS') {
          setLoading(false);
          toast({ title: 'Paiement réussi !', description: 'Votre paiement a été confirmé' });
          onPaymentSuccess();
        } else if (data.type === 'REQUEST_SESSION') {
          // Respond with current session tokens to help the success page authenticate
          const { data: s } = await supabase.auth.getSession();
          const src = (event.source as Window | null);
          if (src && (src as any).postMessage && s.session?.access_token && s.session?.refresh_token) {
            src.postMessage(
              { type: 'SESSION_DATA', session: { access_token: s.session.access_token, refresh_token: s.session.refresh_token } },
              event.origin
            );
          }
        }
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('message', onMessage);

    const paymentChannel = new BroadcastChannel('payment-channel');
    paymentChannel.onmessage = (event) => {
      const data = (event.data || {}) as any;
      if (data.type === 'PAYMENT_SUCCESS') {
        setShowHostedModal(false);
        setHostedCheckoutUrl(null);
        setLoading(false);
        toast({ title: 'Paiement reussi !', description: 'Votre commande a ete confirmee' });
        onPaymentSuccess();
      }
    };

    // Also support BroadcastChannel fallback
    const authSync = new BroadcastChannel('auth-sync');
    authSync.onmessage = async (e) => {
      const data = (e.data || {}) as any;
      if (data.type === 'REQUEST_SESSION') {
        const { data: s } = await supabase.auth.getSession();
        if (s.session?.access_token && s.session?.refresh_token) {
          authSync.postMessage({ type: 'SESSION', session: { access_token: s.session.access_token, refresh_token: s.session.refresh_token } });
        }
      }
    };

    return () => {
      window.removeEventListener('message', onMessage);
      authSync.close();
      paymentChannel.close();
    };
  }, [onPaymentSuccess, toast]);

  const openHostedCheckout = (url: string, provider: 'flutterwave' | 'maishapay' | 'dusupay') => {
    if (!canEmbedHostedCheckout(url)) {
      const confirmed = window.confirm(
        'Le fournisseur de paiement bloque l\'integration dans la page. Voulez-vous continuer dans une fenetre securisee externe ?'
      );

      if (!confirmed) {
        toast({
          title: 'Ouverture annulee',
          description: 'Vous pouvez relancer le paiement quand vous voulez.',
        });
        return;
      }

      const popupOpened = openHostedPopup(url);

      toast({
        title: popupOpened ? 'Fenetre de paiement ouverte' : 'Ouverture externe requise',
        description: popupOpened
          ? 'Le fournisseur de carte bloque l\'iframe. Le paiement continue dans une fenetre securisee.'
          : 'Le fournisseur de carte bloque l\'iframe. Autorisez les popups puis relancez le paiement.',
      });

      if (popupOpened) {
        return;
      }
    }

    const label = provider === 'maishapay' ? 'MaishaPay' : provider === 'dusupay' ? 'Dusupay' : 'Flutterwave';
    setHostedProviderLabel(label);
    setHostedCheckoutUrl(url);
    setShowHostedModal(true);
  };

  const cardFeePercentage =
    cardProvider === 'maishapay'
      ? maishapayCardFeePercentage
      : cardProvider === 'dusupay'
      ? dusupayCardFeePercentage
      : stripeFeePercentage;

  // Calculate fees based on selected method and active provider
  const paymentFees = selectedMethod === 'stripe'
    ? Math.round(totalAmount * (cardFeePercentage / 100))
    : selectedMethod === 'mobile'
    ? Math.round(totalAmount * (mobileMoneyFeePercentage / 100))
    : 0;
  const totalWithFees = totalAmount + paymentFees;

  const handleStripePayment = () => {
    if (!user) {
      toast({
        title: "Erreur",
        description: "Vous devez être connecté pour effectuer un paiement",
        variant: "destructive"
      });
      return;
    }
    
    // If a saved card is selected, pay with it directly
    if (selectedCardId !== 'new') {
      handlePayWithSavedCard();
    } else {
      setShowStripeModal(true);
    }
  };

  const handlePayWithSavedCard = async () => {
    const selectedCard = savedCards.find(c => c.id === selectedCardId);
    if (!selectedCard?.stripe_payment_method_id) {
      toast({
        title: "Erreur",
        description: "Carte invalide. Veuillez en sélectionner une autre.",
        variant: "destructive"
      });
      return;
    }

    const pendingCartData = sessionStorage.getItem('pendingCart');
    console.log('[PayWithSavedCard] pendingCartData:', pendingCartData);
    
    if (!pendingCartData) {
      toast({
        title: "Erreur",
        description: "Données du panier introuvables. Veuillez réessayer.",
        variant: "destructive"
      });
      return;
    }

    const { items, relayId, clientId } = JSON.parse(pendingCartData);
    console.log('[PayWithSavedCard] Parsed data:', { items, relayId, clientId });

    if (!items || items.length === 0) {
      toast({
        title: "Erreur",
        description: "Le panier est vide",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const amountForStripe = convertPrice(totalWithFees, displayCurrency, 'USD');
      console.log('[PayWithSavedCard] Calling function with:', {
        paymentMethodId: selectedCard.stripe_payment_method_id,
        cartItemsCount: items.length,
        totalAmount: totalWithFees,
        amountForStripe,
        relayId,
        clientId
      });

      const { data, error } = await supabase.functions.invoke('pay-with-saved-card', {
        body: {
          paymentMethodId: selectedCard.stripe_payment_method_id,
          cartItems: items,
          relayId,
          clientId,
          totalAmount: totalWithFees,
          amountForStripe: amountForStripe,
          currencyForStripe: 'usd',
          originalCurrency: displayCurrency
        }
      });

      console.log('[PayWithSavedCard] Response:', { data, error });

      if (error) throw error;

      if (data?.success) {
        toast({
          title: 'Paiement réussi !',
          description: 'Votre commande a été confirmée'
        });
        onPaymentSuccess();
      } else if (data?.requiresAction) {
        // 3D Secure required - open Stripe modal for confirmation
        toast({
          title: "Confirmation requise",
          description: "Veuillez confirmer le paiement avec votre banque",
        });
        setShowStripeModal(true);
      } else {
        throw new Error(data?.error || 'Échec du paiement');
      }
    } catch (error: any) {
      console.error('[PayWithSavedCard] Error:', error);
      toast({
        title: "Erreur de paiement",
        description: error.message || "Impossible de traiter le paiement",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStripeSuccess = () => {
    setShowStripeModal(false);
    toast({
      title: 'Paiement réussi !',
      description: 'Votre commande a été confirmée'
    });
    onPaymentSuccess();
  };

  const cleanPhone = phoneNumber.replace(/\s/g, '');
  const isValidMobilePhone = /^\+?243[0-9]{9}$/.test(cleanPhone) || /^0[0-9]{9}$/.test(cleanPhone);

  let pendingCart: any = null;
  const pendingCartRaw = sessionStorage.getItem('pendingCart');
  if (pendingCartRaw) {
    try {
      pendingCart = JSON.parse(pendingCartRaw);
    } catch {
      pendingCart = null;
    }
  }

  const pendingItems = pendingCart?.items;
  const pendingRelayId = pendingCart?.relayId;
  const pendingClientId = pendingCart?.clientId;
  const hasPendingCartData = Boolean(pendingItems?.length && pendingRelayId && pendingClientId);

  const getMobilePaymentOptions = () => 'mobilemoney'; // kept for potential future use

  const stopPolling = () => {
    if (pollIntervalRef.current) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
    if (pollTimeoutRef.current)  { clearTimeout(pollTimeoutRef.current);   pollTimeoutRef.current = null; }
  };

  const isAsyncPendingLikeMessage = (message?: string) => {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return [
      'not completed yet',
      'payment is not completed',
      'en cours',
      'pending',
      'processing',
      'still processing',
      'awaiting',
      'not confirmed',
    ].some((token) => normalized.includes(token));
  };

  // Sandbox-only: simulate the customer confirming the USSD/PIN prompt on their phone.
  // Calls api-payments-verify with the bypass flag so orders are created from backend metadata.
  const simulateUssdConfirmation = async () => {
    if (!pollTxRef) return;
    console.log('[Mobile Payment] Simulating USSD confirmation for', pollTxRef);
    stopPolling();
    setPollMessage('Confirmation simulée en cours...');
    try {
      const { data, error } = await supabase.functions.invoke('api-payments-verify', {
        body: {
          provider: pollProvider,
          txRef: pollTxRef,
          sandboxBypass: true,
          cartItems: pendingItems,
          relayId: pendingRelayId,
          clientId: pendingClientId,
        },
      });
      if (error) throw new Error(error.message);
      if (data?.success) {
        setPollState(null);
        setPollMessage('');
        setLoading(false);
        toast({ title: 'Paiement confirmé !', description: 'Votre commande a été validée.' });
        onPaymentSuccess();
      } else {
        throw new Error(data?.message || 'Échec de la simulation');
      }
    } catch (err: any) {
      console.error('[Mobile Payment] Sandbox simulate failed:', err);
      setPollState('failed');
      setPollMessage(err?.message || 'Erreur lors de la simulation');
      setLoading(false);
    }
  };

  const startPolling = (txRef: string, provider: 'flutterwave' | 'maishapay' | 'dusupay') => {
    stopPolling();
    let attempts = 0;
    const MAX_ATTEMPTS = 36; // 3 minutes at 5s intervals

    pollIntervalRef.current = setInterval(async () => {
      attempts++;
      try {
        const { data } = await supabase.functions.invoke('api-payments-verify', {
          body: { provider, txRef },
        });

        if (data?.success) {
          stopPolling();
          setPollState(null);
          setLoading(false);
          setPollMessage('');
          toast({ title: 'Paiement confirmé !', description: 'Votre commande a été validée.' });
          onPaymentSuccess();
        } else if (data?.pending === false) {
          const backendMessage = data?.message ? String(data.message) : '';

          // Some providers can briefly report non-final states before webhook/order reconciliation.
          // Keep waiting when backend message is clearly "still processing".
          if (isAsyncPendingLikeMessage(backendMessage) && attempts < MAX_ATTEMPTS) {
            setPollState('polling');
            if (backendMessage) setPollMessage(backendMessage);
          } else {
            // Transaction failed or cancelled (final)
            stopPolling();
            setPollState('failed');
            if (backendMessage) setPollMessage(backendMessage);
            setLoading(false);
          }
        } else if (attempts >= MAX_ATTEMPTS) {
          stopPolling();
          setPollState('timeout');
          if (data?.message) {
            setPollMessage(String(data.message));
          } else {
            setPollMessage('Paiement en cours de validation. Si vous avez déjà confirmé, patientez un instant puis vérifiez vos commandes.');
          }
          setLoading(false);
        }
      } catch {
        // network error — keep polling
      }
    }, 5000);
  };

  const handleMobileDirectCharge = async () => {
    if (!user || !mobileProvider || !phoneNumber || !isValidMobilePhone) {
      toast({
        title: 'Informations manquantes',
        description: 'Veuillez vérifier opérateur et numéro mobile.',
        variant: 'destructive',
      });
      return;
    }

    if (!hasPendingCartData) {
      toast({
        title: 'Données de panier manquantes',
        description: 'Veuillez fermer puis réouvrir le panier, puis réessayer.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setPollState(null);
    setPollMessage('');
    try {
      const payload = {
        cartItems: pendingItems,
        relayId: pendingRelayId,
        clientId: pendingClientId,
        totalAmount: totalWithFees,
        originalCurrency: displayCurrency,
        paymentType: 'mobilemoney',
        mobileNetwork: mobileProvider,
        phoneNumber: cleanPhone,
      };

      console.log('[Mobile Payment] Sending to api-payments-initialize:', payload);

      const { data, error } = await supabase.functions.invoke('api-payments-initialize', {
        body: payload,
      });

      console.log('[Mobile Payment] Response:', data, error);

      if (error) {
        const detailedMessage = await extractEdgeFunctionErrorMessage(error, 'Initialisation du paiement mobile impossible');
        throw new Error(detailedMessage);
      }

      const resolvedProvider =
        data?.provider === 'maishapay' ? 'maishapay' :
        data?.provider === 'dusupay' ? 'dusupay' :
        'flutterwave';
      setPollProvider(resolvedProvider);

      // Hosted checkout link (provider-specific)
      if (data?.paymentLink) {
        console.log('[Mobile Payment] Opening embedded hosted checkout:', data.paymentLink);
        openHostedCheckout(data.paymentLink, resolvedProvider);
        return;
      }

      if (data?.requiresPolling && data?.transactionRef) {
        setPollTxRef(data.transactionRef);
        setPollState('polling');
        setSandboxBypassEnabled(!!data?.sandboxBypass);
        setPollMessage(
          data?.providerResponse ||
          data?.message ||
          'Invite envoyee. Validez le paiement sur votre telephone.'
        );
        // Only poll if sandbox bypass is NOT active — in bypass mode the
        // "Simuler" button is the sole confirmation mechanism.
        if (!data?.sandboxBypass) {
          startPolling(data.transactionRef, resolvedProvider);
        }
        return;
      }

      // Sandbox OTP redirect mode: Flutterwave returns a URL to confirm the test payment
      if (data?.redirectUrl) {
        console.log('[Mobile Payment] Sandbox redirect to OTP page:', data.redirectUrl);
        window.location.href = data.redirectUrl;
        return;
      }

      if (data?.chargeStatus === 'successful' && data?.transactionRef) {
        // Payment confirmed — verify through backend to create orders
        console.log('[Mobile Payment] Charge successful, verifying to create orders...');
        const verifyBody: Record<string, unknown> = {
          provider: resolvedProvider,
          txRef: data.transactionRef,
          // Always send cart context so backend can create orders even if
          // MaishaPay verify API is unreachable or returns unexpected status.
          cartItems: pendingItems,
          relayId: pendingRelayId,
          clientId: pendingClientId,
          checkoutCreatedAt: Date.now(),
        };
        const { data: verifyData, error: verifyError } = await supabase.functions.invoke('api-payments-verify', {
          body: verifyBody,
        });
        console.log('[Mobile Payment] Verify response:', verifyData, verifyError);
        if (verifyData?.success) {
          toast({ title: 'Paiement confirmé !', description: 'Votre commande a été validée.' });
          setLoading(false);
          onPaymentSuccess();
          return;
        }
        // Verify returned an error or pending — fall through to polling
        if (verifyData?.pending) {
          setPollTxRef(data.transactionRef);
          setPollState('polling');
          setPollMessage('Confirmation en cours...');
          startPolling(data.transactionRef, resolvedProvider);
          return;
        }
        throw new Error(verifyError?.message || verifyData?.message || 'Erreur de vérification du paiement');
      }


      const errorMsg = data?.error || data?.details || data?.message || 'Initialisation du paiement mobile impossible';
      throw new Error(errorMsg);
    } catch (error: any) {
      const errorMsg = error?.message || 'Impossible de traiter le paiement mobile';
      console.error('[Mobile Payment] Error:', errorMsg, error);
      
      toast({
        title: 'Erreur de paiement',
        description: errorMsg,
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handleAlternativeCardPayment = async () => {
    if (!user) {
      toast({
        title: 'Erreur',
        description: 'Vous devez être connecté pour effectuer un paiement',
        variant: 'destructive',
      });
      return;
    }

    if (!hasPendingCartData) {
      toast({
        title: 'Données de panier manquantes',
        description: 'Veuillez fermer puis réouvrir le panier, puis réessayer.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    setPollState(null);
    setPollMessage('');

    try {
      const payload = {
        cartItems: pendingItems,
        relayId: pendingRelayId,
        clientId: pendingClientId,
        totalAmount: totalWithFees,
        originalCurrency: displayCurrency,
        paymentType: 'card',
      };

      const { data, error } = await supabase.functions.invoke('api-payments-initialize', {
        body: payload,
      });

      if (error) {
        const detailedMessage = await extractEdgeFunctionErrorMessage(error, 'Initialisation paiement carte impossible');
        throw new Error(detailedMessage);
      }

      const resolvedProvider =
        data?.provider === 'maishapay' ? 'maishapay' :
        data?.provider === 'dusupay' ? 'dusupay' :
        'flutterwave';

      setPollProvider(resolvedProvider);

      if (data?.paymentLink) {
        openHostedCheckout(data.paymentLink, resolvedProvider);
        return;
      }

      if (data?.requiresPolling && data?.transactionRef) {
        setPollTxRef(data.transactionRef);
        setPollState('polling');
        setPollMessage(data?.message || 'Confirmation en cours...');
        startPolling(data.transactionRef, resolvedProvider);
        return;
      }

      throw new Error(data?.error || data?.details || data?.message || 'Paiement carte alternatif indisponible');
    } catch (error: any) {
      toast({
        title: 'Erreur de paiement',
        description: error?.message || 'Impossible de traiter le paiement carte',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (selectedMethod === 'stripe') {
      if (cardProvider === 'stripe') {
        handleStripePayment();
      } else {
        handleAlternativeCardPayment();
      }
    }
  };

  return (
    <div className="space-y-4">
      {!user && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-6">
            <div className="text-center space-y-2">
              <p className="text-sm text-destructive font-medium">
                {t('cart.loginRequired')}
              </p>
              <p className="text-xs text-muted-foreground">
                {t('cart.loginRequiredDesc')}
              </p>
              <Button
                variant="outline"
                className="mt-2"
                onClick={() => window.location.href = '/login'}
              >
                Se connecter
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      <div className="text-center space-y-2 sticky top-0 bg-background/95 backdrop-blur-sm p-2 -mx-2 z-10">
        <h3 className="text-lg font-semibold mb-1">Choisissez votre mode de paiement</h3>
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm">
            Sous-total : <span className="font-semibold text-foreground">{formatDisplay(totalAmount)}</span>
          </p>
          {paymentFees > 0 && (
            <p className="text-muted-foreground text-xs">
              Frais de transaction ({selectedMethod === 'stripe' ? cardFeePercentage : mobileMoneyFeePercentage}%) : <span className="font-semibold text-foreground">{formatDisplay(paymentFees)}</span>
            </p>
          )}
          <p className="text-lg font-bold text-foreground">
            Total à payer : {formatDisplay(totalWithFees)}
          </p>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className={`grid gap-4 ${availableProvider === 'both' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
        {/* Stripe Card */}
        {(availableProvider === 'both' || availableProvider === 'stripe') && <Card 
          className={`cursor-pointer transition-all ${
            selectedMethod === 'stripe' 
              ? 'ring-2 ring-primary border-primary bg-primary/5' 
              : 'hover:border-primary/50'
          }`}
          onClick={() => setSelectedMethod('stripe')}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <CardTitle className="text-base">{t('payment.bankCard')}</CardTitle>
              </div>
              {selectedMethod === 'stripe' && (
                <CheckCircle className="h-5 w-5 text-primary" />
              )}
            </div>
            <CardDescription>
              {cardProvider === 'stripe' ? 'Visa, Mastercard, American Express' : `Paiement carte via ${cardProvider === 'dusupay' ? 'Dusupay' : 'MaishaPay'}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="h-4 w-4 text-success" />
              <span className="text-sm text-muted-foreground">
                {cardProvider === 'stripe' ? t('payment.securePayment') : `Paiement securise par ${cardProvider === 'dusupay' ? 'Dusupay' : 'MaishaPay'}`}
              </span>
            </div>
            {(() => {
              const defCard = savedCards.find(c => c.is_default) || savedCards[0];
              return defCard ? (
                <div className="flex items-center gap-2 mt-2 bg-muted/50 rounded-md px-3 py-1.5">
                  <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium capitalize">{defCard.card_brand || 'Carte'}</span>
                  <span className="text-sm text-muted-foreground tracking-widest">•••• {defCard.last_four}</span>
                  {defCard.is_default && <Badge variant="secondary" className="text-xs ml-auto">Par défaut</Badge>}
                </div>
              ) : (
                <Badge variant="secondary" className="bg-success/10 text-success">
                  {t('payment.instantPayment')}
                </Badge>
              );
            })()}
          </CardContent>
        </Card>}

        {/* Mobile Payment Card */}
        {availableProvider !== 'stripe' && <Card 
          className={`cursor-pointer transition-all ${
            selectedMethod === 'mobile' 
              ? 'ring-2 ring-primary border-primary bg-primary/5' 
              : 'hover:border-primary/50'
          }`}
          onClick={() => setSelectedMethod('mobile')}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Smartphone className="h-5 w-5 text-primary" />
                <CardTitle className="text-base">{t('payment.mobileMoney')}</CardTitle>
              </div>
              {selectedMethod === 'mobile' && (
                <CheckCircle className="h-5 w-5 text-primary" />
              )}
            </div>
            <CardDescription>
              Orange Money, Airtel Money, M-Pesa
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center space-x-2 mb-2">
              <Shield className="h-4 w-4 text-success" />
              <span className="text-sm text-muted-foreground">{t('payment.secureMobilePayment')}</span>
            </div>
            <Badge variant="secondary" className="bg-orange-100 text-orange-800">
              {t('payment.popularInDRC')}
            </Badge>
          </CardContent>
        </Card>}
      </div>

      {/* Saved Cards Selection - only show when Stripe is selected */}
      {cardProvider === 'stripe' && (availableProvider === 'both' || availableProvider === 'stripe') && selectedMethod === 'stripe' && user && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Choisir une carte</CardTitle>
            <CardDescription>
              Utilisez une carte enregistrée ou ajoutez-en une nouvelle
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingCards ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <RadioGroup value={selectedCardId} onValueChange={setSelectedCardId} className="space-y-3">
                {savedCards.map((card) => (
                  <div key={card.id} className="flex items-center space-x-3">
                    <RadioGroupItem value={card.id} id={card.id} />
                    <Label htmlFor={card.id} className="flex items-center gap-3 cursor-pointer flex-1 bg-muted/40 hover:bg-muted/70 rounded-lg px-3 py-2 transition-colors">
                      <CreditCard className="h-5 w-5 text-muted-foreground shrink-0" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium capitalize">{card.card_brand || 'Carte'}</span>
                        <span className="text-xs text-muted-foreground tracking-widest">•••• •••• •••• {card.last_four}</span>
                      </div>
                      {card.is_default && (
                        <Badge variant="secondary" className="text-xs ml-auto">Par défaut</Badge>
                      )}
                    </Label>
                  </div>
                ))}
                <div className="flex items-center space-x-3">
                  <RadioGroupItem value="new" id="new-card" />
                  <Label htmlFor="new-card" className="flex items-center space-x-2 cursor-pointer flex-1">
                    <Plus className="h-4 w-4 text-primary" />
                    <span className="text-primary">Ajouter une nouvelle carte</span>
                  </Label>
                </div>
              </RadioGroup>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mobile Payment Details */}
      {selectedMethod === 'mobile' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Informations de paiement mobile</CardTitle>
            <CardDescription>
              Sélectionnez votre opérateur et saisissez votre numéro
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mobile-provider">Opérateur mobile</Label>
              <Select value={mobileProvider} onValueChange={setMobileProvider}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisissez votre opérateur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="orange">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                      <span>Orange Money</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="airtel">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full bg-red-500"></div>
                      <span>Airtel Money</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="mpesa">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 rounded-full bg-green-600"></div>
                      <span>Vodacom M-Pesa</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone-number">Numéro de téléphone</Label>
              <Input
                id="phone-number"
                type="tel"
                autoComplete="off"
                inputMode="tel"
                placeholder="+243 XXX XXX XXX"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Le numéro associé à votre compte mobile money
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Actions */}
      <div className="flex space-x-3">
        <Button 
          variant="outline" 
          className="flex-1"
          onClick={onCancel}
          disabled={loading}
        >
          Annuler
        </Button>
        {selectedMethod === 'stripe' && (
          <Button 
            type="button"
            className="flex-1 bg-gradient-primary"
            onClick={handlePayment}
            disabled={!user || loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading 
              ? t('payment.processing') 
              : (cardProvider === 'stripe' && selectedCardId !== 'new' 
                  ? `Payer avec •••• ${savedCards.find(c => c.id === selectedCardId)?.last_four || ''}`
                  : t('payment.payByCard'))
            }
          </Button>
        )}

        {selectedMethod === 'mobile' && (
          <Button
            type="button"
            className="flex-1 bg-gradient-primary"
            onClick={handleMobileDirectCharge}
            disabled={
              !user ||
              loading ||
              !mobileProvider ||
              !phoneNumber ||
              !isValidMobilePhone ||
              !hasPendingCartData ||
              pollState === 'polling'
            }
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? t('payment.processing') : t('payment.payByMobile')}
          </Button>
        )}
      </div>

      {selectedMethod === 'mobile' && !FLUTTERWAVE_PUBLIC_KEY && pollProvider === 'flutterwave' && (
        <p className="text-xs text-muted-foreground text-center">
          Le mode popup est indisponible sur cette instance. Paiement mobile via page securisee Flutterwave.
        </p>
      )}

      {/* Polling overlay — shown after direct charge while waiting for phone confirmation */}
      {pollState === 'polling' && (
        <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20">
          <CardContent className="pt-6 pb-4 text-center space-y-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
            <p className="font-semibold text-blue-900 dark:text-blue-100">
              En attente de confirmation
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Une invite a été envoyée sur votre téléphone ({cleanPhone}).<br />
              Confirmez le paiement sur votre appareil pour finaliser la commande.
            </p>
            {pollMessage && (
              <p className="text-xs text-blue-700 dark:text-blue-300">Statut operateur: {pollMessage}</p>
            )}
            {sandboxBypassEnabled && (
              <div className="pt-2 border-t border-blue-200 dark:border-blue-800 space-y-2">
                <p className="text-xs italic text-blue-700 dark:text-blue-300">
                  🧪 Mode test sandbox — aucun vrai SMS envoyé
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={simulateUssdConfirmation}
                >
                  Simuler la validation USSD (test)
                </Button>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => { stopPolling(); setPollState(null); setLoading(false); setPollMessage(''); }}
            >
              Annuler l'attente
            </Button>
          </CardContent>
        </Card>
      )}

      {pollState === 'timeout' && (
        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="pt-6 pb-4 text-center space-y-2">
            <p className="font-semibold text-amber-900 dark:text-amber-100">Délai dépassé</p>
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Aucune confirmation reçue après 3 minutes. Vérifiez votre solde et réessayez.
            </p>
            {pollTxRef && <p className="text-xs text-amber-700 dark:text-amber-300">Reference: {pollTxRef}</p>}
            <Button variant="outline" size="sm" onClick={() => { setPollState(null); setPollMessage(''); }}>Fermer</Button>
          </CardContent>
        </Card>
      )}

      {pollState === 'failed' && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-6 pb-4 text-center space-y-2">
            <p className="font-semibold text-destructive">Paiement refusé</p>
            <p className="text-sm text-muted-foreground">
              La transaction a été annulée ou refusée. Vérifiez votre solde et réessayez.
            </p>
            {pollMessage && <p className="text-xs text-muted-foreground">Detail: {pollMessage}</p>}
            <Button variant="outline" size="sm" onClick={() => { setPollState(null); setPollMessage(''); }}>Fermer</Button>
          </CardContent>
        </Card>
      )}

      {cardProvider === 'stripe' && selectedMethod === 'stripe' && selectedCardId === 'new' && (
        <p className="text-xs text-muted-foreground text-center">
          Vous serez redirigé vers une page de paiement sécurisée Stripe
        </p>
      )}
      
      {cardProvider === 'stripe' && selectedMethod === 'stripe' && selectedCardId !== 'new' && (
        <p className="text-xs text-muted-foreground text-center">
          Le paiement sera effectué directement avec votre carte enregistrée
        </p>
      )}

      {cardProvider !== 'stripe' && selectedMethod === 'stripe' && (
        <p className="text-xs text-muted-foreground text-center">
          Le paiement tente l'ouverture en fenetre integree securisee {cardProvider === 'dusupay' ? 'Dusupay' : 'MaishaPay'}. Si le fournisseur bloque l'iframe, une fenetre externe securisee sera proposee.
        </p>
      )}

      {selectedMethod === 'mobile' && (
        <div className="space-y-3">
          <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <p className="text-sm text-blue-900 dark:text-blue-100 font-medium mb-2">
              📱 Comment ça marche :
            </p>
            <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
              <li>Cliquez sur "Payer par mobile"</li>
              <li>Vous recevrez un SMS de confirmation sur votre numéro</li>
              <li>Composez le code USSD reçu ou validez depuis votre app mobile money</li>
              <li>Votre commande sera confirmée automatiquement</li>
            </ol>
          </div>
          
          <div className="bg-amber-50 dark:bg-amber-950/20 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-xs text-amber-900 dark:text-amber-100">
              ⚠️ Assurez-vous d'avoir un solde suffisant sur votre compte mobile money
            </p>
          </div>
        </div>
      )}

      {cardProvider === 'stripe' && (
        <StripeEmbeddedCheckout
          isOpen={showStripeModal}
          onClose={() => setShowStripeModal(false)}
          totalAmount={totalWithFees}
          amountForStripe={convertPrice(totalWithFees, displayCurrency, 'USD')}
          currencyForStripe="usd"
          originalCurrency={displayCurrency}
          onSuccess={handleStripeSuccess}
        />
      )}

      <HostedPaymentCheckout
        isOpen={showHostedModal}
        onClose={() => {
          setShowHostedModal(false);
          setHostedCheckoutUrl(null);
        }}
        checkoutUrl={hostedCheckoutUrl}
        providerLabel={hostedProviderLabel}
      />

    </div>
  );
};

export default PaymentMethod;